import { LOCALE_COOKIE } from '@src/config/localisation/locale-config.static-constants'
import type { TAbsoluteLocale, TCountry } from '@src/config/localisation/locale-config.types'
import { getIsCountryCompliant } from '@src/modules/global-state/locale-config.browser'
import { USER_CONSENT_COOKIES } from '@src/quince-site-common/src/utils/constants'
import getCookie from '@src/quince-site-common/src/utils/getCookie'
import { UserAgnosticGlobalFeatureGates } from '@src/server-side/api/feature-gates.types'
import { getStoreProgramIdFromStorage, isB2BSelfServeDomain } from '@src/utils/b2b'
import { executeFireAndForgotTask } from '@src/utils/fire-and-forgot-task'
import { parseLocale } from '@src/utils/i18n.utils'
import { safeSync } from '@src/utils/safe'

import { Attentive } from './integrations/Attentive'
import { Claritas } from './integrations/Claritas'
import { CriteoAnalytics } from './integrations/CriteoAnalytics'
import { DatalayerPixel } from './integrations/DatalayerPixel'
import { EdgeTag } from './integrations/edge-tag'
import { FbPixel } from './integrations/FbPixel'
import { GoogleAdsTag } from './integrations/GoogleAdsTag'
import { GoogleAnalytics } from './integrations/GoogleAnalytics'
import { GoogleAnalytics4 } from './integrations/GoogleAnalytics4'
import { GoogleAnalytics4 as GoogleAnalyticsWebContainer } from './integrations/GoogleWebGtm'
import { Impact } from './integrations/Impact'
import { InternalAnalytics } from './integrations/InternalAnalytics'
import { LinkedInPixel } from './integrations/LinkedIn'
// import { LiveConnect } from './integrations/LiveConnect'
import { PebblePost } from './integrations/PebblePost'
import { PinterestTag } from './integrations/PinterestTag'
import { Poplar } from './integrations/Poplar'
import { RetentionTracking } from './integrations/Retention'
import { ServerSGTMPixel } from './integrations/ServerSGTMPixel'
import { SnapchatPixel } from './integrations/SnapchatPixel'
// import { StylightPixel } from './integrations/StylightPixel'
import { Stylitics } from './integrations/Stylitics'
import { Tatari } from './integrations/Tatari'
import { TikTokPixel } from './integrations/TikTokPixel'
import TrustArcCl from './integrations/trustarc'
import { TelemetryIntegration } from './telemetry-events.types'

const COUNTRY_EXCLUDED_INTEGRATIONS: Partial<Record<TCountry, string[]>> = {
  us: [],
  ca: [
    'pebblePost',
    'poplar',
    'CriteoAnalytics',
    'Claritas',
    'linkedInPixel',
    'lc',
    'retentionTracking',
    'scp',
    'styl',
    'stylitics',
    'Tatari',
  ],
  // UK: only Google Analytics (all containers), Meta (fbPixel), Impact, and
  // Attentive are permitted. All other 3P ad/tracking pixels are excluded even
  // when the user has granted advertising consent, until explicitly approved by
  // the UK compliance review.
  gb: [
    'claritas',
    'criteoAnalytics',
    'googleAdsTag',
    'linkedInPixel',
    'pebblePost',
    'poplar',
    'pinterestTag',
    'retentionTracking',
    'scp',
    'tatari',
    'tikTokPixel',
    'edgeTag',
    // DatalayerPixel pushes dl_* events to GTM dataLayer. GTM tags (Twitter pixel,
    // Bing UET etc.) are not individually UK-gated in GTM console yet — excluding
    // here prevents out-of-scope pixels firing for UK until GTM tags are configured
    // with per-tag UK consent conditions.
    'datalayerPixel',
  ],
}

const TELEMETRY_INTEGRATION_REGISTRY_MAP_GETTER = () => {
  if (getStoreProgramIdFromStorage() && !isB2BSelfServeDomain())
    return {
      internalAnalytics: InternalAnalytics,
    }

  return {
    attentive: Attentive,
    claritas: Claritas,
    criteoAnalytics: CriteoAnalytics,
    fbPixel: FbPixel,
    googleAnalytics: GoogleAnalytics,
    googleAnalytics4: GoogleAnalytics4,
    googleAdsTag: GoogleAdsTag,
    googleWebContainer: GoogleAnalyticsWebContainer,
    internalAnalytics: InternalAnalytics,
    impact: Impact,
    linkedInPixel: LinkedInPixel,
    // lc: LiveConnect,
    pebblePost: PebblePost, // only us specific
    poplar: Poplar, // only us specific
    pinterestTag: PinterestTag,
    retentionTracking: RetentionTracking,
    scp: SnapchatPixel,
    // styl: StylightPixel,
    tatari: Tatari,
    tikTokPixel: TikTokPixel,
    datalayerPixel: DatalayerPixel,
    serverSGTMPixel: ServerSGTMPixel,
    edgeTag: EdgeTag,
    stylitics: Stylitics,
  }
}

let telemetryIntegrationRegistry: [string, TelemetryIntegration][] = []

function getTelemetryIntegrationRegistry() {
  if (telemetryIntegrationRegistry.length > 0) {
    return telemetryIntegrationRegistry
  }
  telemetryIntegrationRegistry = Object.entries(TELEMETRY_INTEGRATION_REGISTRY_MAP_GETTER())
  return telemetryIntegrationRegistry
}

export function getAllDomains(locale: TAbsoluteLocale, isI18nPreviewEnabled: boolean): string[] {
  const domains = new Set<string>()
  const { country } = parseLocale(locale)

  const excluded = COUNTRY_EXCLUDED_INTEGRATIONS[country] || []

  getTelemetryIntegrationRegistry().forEach(([_integrationKey, integration]: [string, TelemetryIntegration]) => {
    if ((!isI18nPreviewEnabled || !excluded.includes(_integrationKey)) && integration.config.domain) {
      domains.add(integration.config.domain)
    }
  })
  return Array.from(domains)
}

/**
 * Returns true when we are in a countryCompliant locale (e.g. UK) AND the user
 * has NOT explicitly granted advertising consent via TrustArc.
 * When true, ALL 3rd-party SDK initialisations must be blocked.
 *
 * Exported so callers that initialise SDKs outside forEachTelemetryIntegration
 * (e.g. Attentive direct SDK load in client-entry) can apply the same gate.
 */
export function isAdvertisingConsentRequired(): boolean {
  if (typeof document === 'undefined') return false
  if (!getIsCountryCompliant()) return false
  const advertisingConsent = getCookie(USER_CONSENT_COOKIES.ADVERTISING)
  return advertisingConsent !== 'true'
}

/**
 * Returns true for countryCompliant locales (e.g. UK) regardless of consent
 * state. Use for things that are allowed without consent and only depend on the
 * locale — e.g. seeding the pseudonymous Mixpanel `_mp_user_device_id`, which is
 * needed on all UK SGTM events (pre- and post-consent). Consent gating belongs
 * on identity (`$user_id`), not on the device id.
 */
export function isCountryCompliantLocale(): boolean {
  if (typeof document === 'undefined') return false
  return getIsCountryCompliant()
}

/**
 * US/CA only — called only after the countryCompliant UK gate in
 * forEachTelemetryIntegration has already passed. Checks per-domain TrustArc
 * CCM consent before firing the integration task.
 */
function checkTrusteCCMConsentBeforeInitialization(
  task: (integrationKey: string, integration: TelemetryIntegration) => void | Promise<void>,
  integration: TelemetryIntegration,
  integrationKey: string,
  userAgnosticGlobalFeatureGates: UserAgnosticGlobalFeatureGates | null,
) {
  TrustArcCl.checkTrusteCCMConsent(integration.config.domain ?? '')
    .then(hasConsent => {
      if (!hasConsent) return
      if (!integration.config.enabled) return

      // Check if feature gate is enabled for this integration
      if (
        integration.config.featureGateName &&
        userAgnosticGlobalFeatureGates &&
        !userAgnosticGlobalFeatureGates[integration.config.featureGateName]
      ) {
        return
      }

      executeFireAndForgotTask(() => {
        return task(integrationKey, integration)
      }, integration.config.eventIdealTimeout)
    })
    .catch((err: unknown) => {
      console.error('Error checking Truste CCM Consent', err)
    })
}

export function forEachTelemetryIntegration(
  task: (integrationKey: string, integration: TelemetryIntegration) => void | Promise<void>,
  userAgnosticGlobalFeatureGates: UserAgnosticGlobalFeatureGates | null,
) {
  // Resolve locale compliance once — shared across all integrations in this dispatch.
  const locale = getCookie(LOCALE_COOKIE) ?? ''
  const isCountryCompliant = getIsCountryCompliant()
  const hasAdvertisingConsent = getCookie(USER_CONSENT_COOKIES.ADVERTISING) === 'true'

  getTelemetryIntegrationRegistry().forEach(
    safeSync(([integrationKey, integration]: [string, TelemetryIntegration]) => {
      // ── countryCompliant locales (e.g. UK / en-GB) ────────────────────────
      // Gate 1: blanket advertising consent cookie (user_advertising_consent)
      // Gate 2: per-country exclusion list
      // Gate 3: feature gate
      // Gate 4: per-domain TrustArc CCM consent check (same as US/CA)
      // No change for US / CA — they always skip this block.
      if (isCountryCompliant) {
        if (!integration.config.enabled) return

        // first-party integrations (skipConsentCheck) fire without an advertising-consent
        // gate, but still respect their feature-gate kill-switch — same as the US/CA
        // skipConsentCheck path, so a gate like ssr_telemetry_internal_analytics stops
        // the integration consistently across all locales.
        if (integration.config.skipConsentCheck) {
          if (
            integration.config.featureGateName &&
            userAgnosticGlobalFeatureGates &&
            !userAgnosticGlobalFeatureGates[integration.config.featureGateName]
          ) {
            return
          }
          task(integrationKey, integration)?.catch((err: unknown) => {
            console.error('Error firing telemetry event', err)
          })
          return
        }

        // 3P integrations: blanket advertising consent gate for UK
        if (!hasAdvertisingConsent) return

        // Apply per-country pixel allowlist
        const { country } = parseLocale(locale)
        const countryExcluded = COUNTRY_EXCLUDED_INTEGRATIONS[country] ?? []
        if (countryExcluded.includes(integrationKey)) return

        if (
          integration.config.featureGateName &&
          userAgnosticGlobalFeatureGates &&
          !userAgnosticGlobalFeatureGates[integration.config.featureGateName]
        ) {
          return
        }

        // 3P integrations: per-domain TrustArc CCM consent check (same flow as US/CA)
        checkTrusteCCMConsentBeforeInitialization(task, integration, integrationKey, userAgnosticGlobalFeatureGates)
        return
      }

      // ── US / CA: existing per-domain TrustArc CCM flow ───────────────────
      if (integration.config.skipConsentCheck) {
        if (!integration.config.enabled) return

        if (
          integration.config.featureGateName &&
          userAgnosticGlobalFeatureGates &&
          !userAgnosticGlobalFeatureGates[integration.config.featureGateName]
        ) {
          return
        }

        task(integrationKey, integration)?.catch((err: unknown) => {
          console.error('Error firing telemetry event', err)
        })
        return
      }

      checkTrusteCCMConsentBeforeInitialization(task, integration, integrationKey, userAgnosticGlobalFeatureGates)
    }),
  )
}
